<script lang="ts" setup>
import { computed, onBeforeMount } from 'vue';
import { storeToRefs } from 'pinia';
import { useClipboard } from '@vueuse/core';
import Dialog from 'primevue/dialog';
import OrganizationReferralBuilderIntakeForm from './OrganizationReferralBuilderIntakeForm.vue';
import { useOrganizationReferralBuilderStore } from '@/store';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum';

const organizationReferralBuilderStore = useOrganizationReferralBuilderStore();

const {
  referralBuilderSettings,
  referralBuilderSettingsId,
  referralBuilderSettingsLoading,
  referralCode,
  referralCodeError,
  referralFormId,
  referralFormCodeModal,
  referralFormLinkModal,
  referralFormPreviewModal,
  source,
} = storeToRefs(organizationReferralBuilderStore);

onBeforeMount(() => organizationReferralBuilderStore.getReferralBuilderSettings());

const referralCodeLink = computed(() => `${window.location.origin}/referral/intake?code=${referralFormId.value}`);

const referralFormCode = computed(
  () =>
    '<iframe src="' +
    referralCodeLink.value +
    '" width="100%" height="1055" frameborder="0" marginheight="0" marginwidth="0"></iframe>',
);

const { copied, copy } = useClipboard({ source });
</script>

<template>
  <Card v-if="!referralBuilderSettingsLoading">
    <template #content>
      <div v-if="can([PERMISSIONS.REFERRAL_BUILDER_STORE])" class="flex gap-3 py-3 text-right">
        <div class="flex flex-wrap gap-3 flex-1 text-left">
          <div class="flex">
            <Icon name="info" class="mt-1 text-primary text-xl" />
          </div>
          <div class="flex flex-1">
            <span
              v-text="
                'To build your Referral page, please select the additional data you want to include. You can also choose whether this data should be required. Note that the initial set of data is always included and required for referrals.'
              "
            />
          </div>
        </div>
        <div v-if="referralFormId" class="inline mt-2">
          <Icon
            v-tooltip.top="'Referral Form Code'"
            name="copy"
            class="px-2 text-grey text-xl cursor-pointer"
            @click="organizationReferralBuilderStore.onReferralFormCodeModal"
          />
          <Dialog v-model:visible="referralFormCodeModal" modal header="Referral Form Code" :style="{ width: '50rem' }">
            <div class="w-full pb-3 text-md text-primary font-semibold">
              You can add this form to an existing webpage by editing the webpage and pasting this code in the desired
              position on the page.
            </div>
            <div class="w-full p-4 border-round bg-white text-base">
              <span v-text="referralFormCode" />
            </div>
            <div class="flex flex-row-reverse mt-4">
              <AppButton type="primary" :text="copied ? 'Copied' : 'Copy Code'" @click="copy(referralFormCode)" />
            </div>
          </Dialog>
          <Icon
            v-tooltip.top="'Referral Form Link'"
            name="link"
            class="px-2 text-grey text-xl cursor-pointer"
            @click="organizationReferralBuilderStore.onReferralFormLinkModal"
          />
          <Dialog v-model:visible="referralFormLinkModal" modal header="Referral Form Link" :style="{ width: '50rem' }">
            <div class="w-full p-4 border-round bg-white text-base">
              <span v-text="referralCodeLink" />
            </div>
            <div class="flex flex-row-reverse mt-4">
              <AppButton type="primary" :text="copied ? 'Copied' : 'Copy Link'" @click="copy(referralCodeLink)" />
            </div>
          </Dialog>
          <Icon
            v-tooltip.top="'Preview Referral Form'"
            name="visibility"
            class="px-2 text-grey text-xl cursor-pointer"
            @click="organizationReferralBuilderStore.onReferralFormPreviewModal"
          />
          <Dialog
            v-model:visible="referralFormPreviewModal"
            modal
            header="Referral Form Preview"
            :style="{ width: '50rem' }"
          >
            <OrganizationReferralBuilderIntakeForm
              v-if="referralFormId"
              :referral-form-id="referralFormId"
              :is-preview="true"
            />
          </Dialog>
        </div>
        <AppButton
          v-if="referralFormId"
          type="secondary"
          text="Cancel"
          @click="organizationReferralBuilderStore.onReferralBuilderCancel"
        />
        <AppButton
          type="primary"
          :text="`${!referralBuilderSettingsId ? 'Create' : 'Update'} Settings`"
          @click="organizationReferralBuilderStore.onReferralBuilderSave"
        />
      </div>
      <DataTable :value="referralBuilderSettings">
        <Column field="label" header="Display Label" header-class="w-8">
          <template #body="{ data }">
            <div class="flex">
              <div class="flex-1 flex">
                <span
                  :class="`font-semibold text-primary ${data.label === 'Referral Code' ? 'mt-2' : ''}`"
                  v-text="data.label"
                />
              </div>
              <div v-if="data.label === 'Referral Code'" class="flex-1 flex w-full">
                <div class="flex w-4">
                  <div
                    class="mt-2 mr-2 inline-block text-primary hover:underline"
                    @click="organizationReferralBuilderStore.onGenerateReferralCode"
                  >
                    Generate Code
                  </div>
                </div>
                <div class="flex w-full">
                  <InputText
                    :id="data.id"
                    v-model="referralCode"
                    :disabled="data.disabled"
                    type="text"
                    placeholder="Enter or Generate a Custom Referral Code"
                    :class="`w-full relative ${referralCodeError ? 'p-invalid' : ''}`"
                  />
                </div>
              </div>
            </div>
          </template>
        </Column>
        <Column field="include" header="Include">
          <template #body="{ data }">
            <InputSwitch
              :id="data.id"
              v-model="data.include.enabled"
              :disabled="data.include.disabled"
              class="ml-1"
              @change="organizationReferralBuilderStore.onIncludeChange(data.id)"
            />
          </template>
        </Column>
        <Column field="required" header="Required">
          <template #body="{ data }">
            <InputSwitch
              :id="data.id"
              v-model="data.required.enabled"
              :disabled="data.required.disabled"
              class="ml-1"
              @change="organizationReferralBuilderStore.onRequiredChange(data.id)"
            />
          </template>
        </Column>
      </DataTable>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  .p-datatable {
    .p-row-even,
    .p-row-odd {
      &:hover {
        background-color: var(--surface-50);
        cursor: pointer;
      }
    }
  }
}
</style>
